import React, { useRef, useEffect, useState } from 'react';

import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getBranding } from '../../../services/companyBrandingService';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' })?.format(value || 0);

const formatDate = (dateStr) => {
  if (!dateStr) return new Date()?.toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' });
  return new Date(dateStr)?.toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' });
};

const STATUS_LABELS = {
  draft: 'Borrador',
  enviada: 'Enviada al Cliente',
  aprobada: 'Aprobada',
  rechazada: 'Rechazada',
  en_proceso: 'En Proceso',
};

// ─── Content body (tables, totals, signature) — shared across layouts ────────

const QuotationContent = ({ data, primaryColor }) => {
  const {
    folio,
    fecha,
    clientData,
    vehicleData,
    vehicle,
    client,
    quoteItems,
    quotationStatus,
    signatureDataUrl,
    observations,
  } = data;

  const parts = (quoteItems || [])?.filter(i => i?.type !== 'labor');
  const labor = (quoteItems || [])?.filter(i => i?.type === 'labor');
  const subtotalParts = parts?.reduce((s, i) => s + (i?.price || 0) * (i?.quantity || 1), 0);
  const subtotalLabor = labor?.reduce((s, i) => s + (i?.price || 0) * (i?.quantity || 1), 0);
  const total = subtotalParts + subtotalLabor;

  // Neutral palette for document elements (independent of brand color)
  const headerBarColor = '#999999';   // 60% grey — table header bars
  const labelTextColor = '#444444';   // dark grey — section labels / titles
  const totalBlockColor = '#333333';  // 80% grey — Total block

  return (
    <>
      {/* Client & Vehicle */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
        <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px', backgroundColor: '#ffffff' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: labelTextColor, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Datos del Cliente</div>
          <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '3px' }}>{clientData?.nombre || client || '—'}</div>
          {clientData?.telefono && <div style={{ fontSize: '11px', color: '#555' }}>Tel: {clientData?.telefono}</div>}
          {clientData?.email && <div style={{ fontSize: '11px', color: '#555' }}>{clientData?.email}</div>}
          {clientData?.direccion && <div style={{ fontSize: '11px', color: '#555' }}>{clientData?.direccion}</div>}
        </div>
        <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px', backgroundColor: '#ffffff' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: labelTextColor, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Datos de la Unidad</div>
          <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '3px' }}>
            {vehicleData ? `${vehicleData?.marca || ''} ${vehicleData?.modelo || ''} ${vehicleData?.year || ''}`?.trim() : (vehicle || '—')}
          </div>
          {vehicleData?.placas && <div style={{ fontSize: '11px', color: '#555' }}>Placas: {vehicleData?.placas}</div>}
          {vehicleData?.vin && <div style={{ fontSize: '11px', color: '#555' }}>VIN: {vehicleData?.vin}</div>}
          {vehicleData?.color && <div style={{ fontSize: '11px', color: '#555' }}>Color: {vehicleData?.color}</div>}
        </div>
      </div>

      {/* Folio / date row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
        <div style={{ fontSize: '13px', fontWeight: '700', color: labelTextColor }}>COTIZACIÓN</div>
        <div style={{ fontSize: '11px', color: '#555' }}>
          Folio: <strong>{folio || '—'}</strong> &nbsp;·&nbsp; Fecha: {formatDate(fecha)}
          {quotationStatus && (
            <span style={{ marginLeft: '8px', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600', backgroundColor: headerBarColor, color: '#fff' }}>
              {STATUS_LABELS?.[quotationStatus] || quotationStatus}
            </span>
          )}
        </div>
      </div>

      {/* Parts Table */}
      {parts?.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: labelTextColor, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Refacciones y Materiales</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', backgroundColor: '#ffffff' }}>
            <thead>
              <tr style={{ backgroundColor: headerBarColor, color: '#ffffff' }}>
                <th style={{ padding: '7px 10px', textAlign: 'left', fontWeight: '600' }}>Descripción</th>
                <th style={{ padding: '7px 10px', textAlign: 'center', fontWeight: '600', width: '60px' }}>Cant.</th>
                <th style={{ padding: '7px 10px', textAlign: 'right', fontWeight: '600', width: '110px' }}>P. Unitario</th>
                <th style={{ padding: '7px 10px', textAlign: 'right', fontWeight: '600', width: '110px' }}>Importe</th>
              </tr>
            </thead>
            <tbody>
              {parts?.map((item, idx) => (
                <tr key={item?.id || idx} style={{ backgroundColor: idx % 2 === 0 ? '#f8fafc' : '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '6px 10px' }}>{item?.description || '—'}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'center' }}>{item?.quantity || 1}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>{formatCurrency(item?.price)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: '600' }}>{formatCurrency((item?.price || 0) * (item?.quantity || 1))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Labor Table */}
      {labor?.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: labelTextColor, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Mano de Obra</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', backgroundColor: '#ffffff' }}>
            <thead>
              <tr style={{ backgroundColor: headerBarColor, color: '#ffffff' }}>
                <th style={{ padding: '7px 10px', textAlign: 'left', fontWeight: '600' }}>Servicio</th>
                <th style={{ padding: '7px 10px', textAlign: 'center', fontWeight: '600', width: '70px' }}>Horas</th>
                <th style={{ padding: '7px 10px', textAlign: 'right', fontWeight: '600', width: '110px' }}>P. Hora</th>
                <th style={{ padding: '7px 10px', textAlign: 'right', fontWeight: '600', width: '110px' }}>Importe</th>
              </tr>
            </thead>
            <tbody>
              {labor?.map((item, idx) => (
                <tr key={item?.id || idx} style={{ backgroundColor: idx % 2 === 0 ? '#f8fafc' : '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '6px 10px' }}>{item?.description || '—'}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'center' }}>{item?.quantity || 1}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>{formatCurrency(item?.price)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: '600' }}>{formatCurrency((item?.price || 0) * (item?.quantity || 1))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Totals */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <div style={{ width: '260px', border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
          {parts?.length > 0 && labor?.length > 0 && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', backgroundColor: '#f8fafc', fontSize: '11px' }}>
                <span>Subtotal Refacciones:</span>
                <span style={{ fontWeight: '600' }}>{formatCurrency(subtotalParts)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', backgroundColor: '#f8fafc', fontSize: '11px' }}>
                <span>Subtotal Mano de Obra:</span>
                <span style={{ fontWeight: '600' }}>{formatCurrency(subtotalLabor)}</span>
              </div>
            </>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', backgroundColor: totalBlockColor, color: '#ffffff', fontSize: '14px', fontWeight: '700' }}>
            <span>TOTAL:</span>
            <span>{formatCurrency(total)}</span>
          </div>
        </div>
      </div>

      {/* Observations */}
      {observations && (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px', marginBottom: '16px', backgroundColor: '#ffffff' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: labelTextColor, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Observaciones</div>
          <div style={{ fontSize: '11px', color: '#444', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{observations}</div>
        </div>
      )}

      {/* Signature */}
      {signatureDataUrl && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: labelTextColor, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Autorización del Cliente</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '24px' }}>
            <div style={{ textAlign: 'center' }}>
              <img src={signatureDataUrl} alt="Firma del cliente" style={{ height: '60px', maxWidth: '180px', objectFit: 'contain', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px', backgroundColor: '#fff' }} />
              <div style={{ borderTop: '1px solid #333', marginTop: '4px', paddingTop: '3px', fontSize: '10px', color: '#555' }}>Firma del Cliente</div>
              <div style={{ fontSize: '10px', color: '#555' }}>{data?.clientData?.nombre || data?.client || ''}</div>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: '16px', fontSize: '11px', color: '#888', fontStyle: 'italic' }}>
        Esta cotización tiene una vigencia de 15 días a partir de la fecha de emisión.
      </div>
    </>
  );
};

// ─── Layout: Custom letterhead (uploaded file as background) ─────────────────

const CustomLetterheadDocument = ({ data, branding }) => {
  const primaryColor = branding?.primary_color || '#E53E3E';

  return (
    <div
      id="quotation-pdf-document"
      style={{ width: '794px', minHeight: '1123px', position: 'relative', fontFamily: 'Arial, sans-serif', fontSize: '12px', color: '#1a1a1a', boxSizing: 'border-box' }}
    >
      {/* Background letterhead */}
      <img
        src={branding?.letterhead_url}
        alt="Hoja membretada"
        crossOrigin="anonymous"
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}
      />
      {/* Content area — positioned over the letterhead */}
      <div style={{ position: 'relative', zIndex: 1, padding: '180px 48px 80px 48px' }}>
        <QuotationContent data={data} primaryColor={primaryColor} />
      </div>
    </div>
  );
};

// ─── Layout: Proposal B ──────────────────────────────────────────────────────

const ProposalBDocument = ({ data, branding }) => {
  const primaryColor = branding?.primary_color || '#E53E3E';
  const secondaryColor = branding?.secondary_color || '#1A1A1A';
  const companyName = branding?.business_name || branding?.company_name || 'Taller Automotriz';
  const phone = branding?.phone || '';
  const email = branding?.email || '';
  const website = branding?.website || '';
  const address = branding?.address || '';
  const logo = branding?.logo_url;
  const footer = branding?.footer_text || `${companyName} — Taller Mecánico Automotriz`;

  return (
    <div
      id="quotation-pdf-document"
      style={{ width: '794px', minHeight: '1123px', backgroundColor: '#ffffff', fontFamily: 'Arial, sans-serif', fontSize: '12px', color: '#1a1a1a', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}
    >
      <div style={{ height: '5px', backgroundColor: primaryColor }} />
      <div style={{ padding: '20px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {logo ? (
            <img src={logo} alt="Logo" crossOrigin="anonymous" style={{ height: '56px', width: 'auto', objectFit: 'contain' }} />
          ) : (
            <div style={{ width: '56px', height: '56px', backgroundColor: primaryColor, borderRadius: '6px', opacity: 0.2 }} />
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: secondaryColor }}>{companyName}</div>
          <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>Taller Mecánico Automotriz</div>
          <div style={{ fontSize: '11px', color: '#555', marginTop: '4px' }}>
            {[phone, email, website]?.filter(Boolean)?.join(' · ')}
          </div>
        </div>
      </div>
      <div style={{ flex: 1, padding: '24px 32px' }}>
        <QuotationContent data={data} primaryColor={primaryColor} />
      </div>
      <div style={{ borderTop: `2px solid ${primaryColor}`, padding: '10px 32px', display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#888' }}>
        <span>{footer}</span>
        {address && <span>{address}</span>}
      </div>
    </div>
  );
};

// ─── Layout: Proposal A / default generated ──────────────────────────────────

const ProposalADocument = ({ data, branding }) => {
  const primaryColor = branding?.primary_color || '#E53E3E';
  const secondaryColor = branding?.secondary_color || '#1A1A1A';
  const companyName = branding?.business_name || branding?.company_name || 'Taller Automotriz';
  const address = branding?.address || '';
  const phone = branding?.phone || '';
  const whatsapp = branding?.whatsapp || '';
  const email = branding?.email || '';
  const website = branding?.website || '';
  const logo = branding?.logo_url;
  const footer = branding?.footer_text || `${companyName} — Taller Mecánico Automotriz${phone ? ' — Tel: ' + phone : ''}`;

  return (
    <div
      id="quotation-pdf-document"
      style={{ width: '794px', minHeight: '1123px', backgroundColor: '#ffffff', fontFamily: 'Arial, sans-serif', fontSize: '12px', color: '#1a1a1a', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}
    >
      {/* Header band */}
      <div style={{ backgroundColor: primaryColor, padding: '24px 32px', display: 'flex', alignItems: 'center', gap: '20px' }}>
        {logo ? (
          <img src={logo} alt="Logo" crossOrigin="anonymous" style={{ height: '64px', width: 'auto', objectFit: 'contain', borderRadius: '4px' }} />
        ) : (
          <div style={{ width: '64px', height: '64px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '10px', textAlign: 'center', padding: '4px' }}>LOGO</div>
        )}
        <div style={{ flex: 1 }}>
          <h1 style={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold', margin: 0, lineHeight: 1.2 }}>{companyName}</h1>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', margin: '4px 0 0 0' }}>Taller Mecánico Automotriz</p>
        </div>
      </div>
      {/* Contact row */}
      <div style={{ backgroundColor: '#f8f8f8', borderBottom: `3px solid ${primaryColor}`, padding: '10px 32px', display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '12px', color: '#444' }}>
        {address && <span><span style={{ color: primaryColor, fontWeight: 'bold' }}>📍</span> {address}</span>}
        {phone && <span><span style={{ color: primaryColor, fontWeight: 'bold' }}>📞</span> {phone}</span>}
        {whatsapp && whatsapp !== phone && <span><span style={{ color: primaryColor, fontWeight: 'bold' }}>💬</span> {whatsapp}</span>}
        {email && <span><span style={{ color: primaryColor, fontWeight: 'bold' }}>✉</span> {email}</span>}
        {website && <span><span style={{ color: primaryColor, fontWeight: 'bold' }}>🌐</span> {website}</span>}
      </div>
      {/* Content */}
      <div style={{ flex: 1, padding: '24px 32px', borderLeft: `4px solid ${primaryColor}`, margin: '20px 32px', borderRadius: '2px', backgroundColor: '#fafafa' }}>
        <QuotationContent data={data} primaryColor={primaryColor} />
      </div>
      {/* Footer */}
      <div style={{ backgroundColor: secondaryColor, padding: '14px 32px', marginTop: 'auto' }}>
        <p style={{ color: '#ffffff', fontSize: '11px', margin: 0, textAlign: 'center', opacity: 0.9 }}>{footer}</p>
      </div>
    </div>
  );
};

// ─── Selector: pick the right document layout based on branding config ────────

const QuotationDocument = ({ data, branding }) => {
  // Priority 1: custom uploaded letterhead
  if (branding?.letterhead_url) {
    return <CustomLetterheadDocument data={data} branding={branding} />;
  }
  // Priority 2: selected auto proposal
  if (branding?.selected_proposal === 'propuesta_b') {
    return <ProposalBDocument data={data} branding={branding} />;
  }
  // Default: Proposal A (generated layout with brand colors)
  return <ProposalADocument data={data} branding={branding} />;
};

// ─── Preview Modal ───────────────────────────────────────────────────────────

const QuotationPreviewModal = ({ data, branding, onClose, onGeneratePdf, onPrint, isGenerating }) => {
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/80">
      <div className="flex items-center justify-between bg-gray-900 px-4 py-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Icon name="FileText" size={20} className="text-blue-400" />
          <span className="text-white font-semibold text-sm">Vista previa PDF — Cotización {data?.folio || ''}</span>
          {branding?.letterhead_url && (
            <span className="text-xs text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full">Papelería personalizada</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" iconName="Printer" iconPosition="left" onClick={onPrint} className="text-white border-white/30 hover:bg-white/10 text-xs py-1.5 px-3">
            Imprimir PDF
          </Button>
          <Button variant="default" iconName="Download" iconPosition="left" onClick={onGeneratePdf} disabled={isGenerating} loading={isGenerating} className="text-xs py-1.5 px-3">
            Generar PDF
          </Button>
          <button onClick={onClose} className="ml-2 text-white/70 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors" aria-label="Cerrar">
            <Icon name="X" size={20} />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto bg-gray-700 p-4">
        <div className="shadow-2xl">
          <QuotationDocument data={data} branding={branding} />
        </div>
      </div>
    </div>
  );
};

// ─── Main exported component ─────────────────────────────────────────────────

const QuotationPdfTemplate = ({
  selectedWorkOrder,
  quoteItems,
  quotation,
  signatureDataUrl,
  observations,
  companyId,
  mode,
  onClose,
}) => {
  const [branding, setBranding] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    getBranding(companyId)?.then(b => setBranding(b || {}))?.catch(() => setBranding({}));
  }, [companyId]);

  const documentData = {
    folio: selectedWorkOrder?.folio || quotation?.folio || `COT-${Date.now()?.toString()?.slice(-6)}`,
    fecha: quotation?.created_at || new Date()?.toISOString(),
    clientData: selectedWorkOrder?.clientData || null,
    vehicleData: selectedWorkOrder?.vehicleData || null,
    vehicle: selectedWorkOrder?.vehicle || '',
    client: selectedWorkOrder?.client || '',
    quoteItems: quoteItems || [],
    quotationStatus: quotation?.status || null,
    signatureDataUrl: signatureDataUrl || null,
    observations: observations || '',
  };

  const openPrintWindow = (autoClose = false) => {
    const printContent = document.getElementById('quotation-pdf-document');
    if (!printContent) return;

    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) {
      alert('Por favor permite ventanas emergentes para imprimir/exportar.');
      return;
    }

    printWindow?.document?.write(`
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8" />
        <title>Cotización ${documentData?.folio}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; background: #fff; }
          @page { size: A4 portrait; margin: 0; }
          @media print { html, body { width: 210mm; } }
        </style>
      </head>
      <body>
        ${printContent?.outerHTML}
      </body>
      </html>
    `);
    printWindow?.document?.close();
    printWindow?.focus();
    setTimeout(() => {
      printWindow?.print();
      if (autoClose) printWindow?.close();
    }, 500);
  };

  const handleGeneratePdf = () => {
    setIsGenerating(true);
    try {
      openPrintWindow(false);
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePrint = () => openPrintWindow(false);

  if (mode === 'preview') {
    return (
      <QuotationPreviewModal
        data={documentData}
        branding={branding || {}}
        onClose={onClose}
        onGeneratePdf={handleGeneratePdf}
        onPrint={handlePrint}
        isGenerating={isGenerating}
      />
    );
  }

  if (mode === 'generate') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (branding !== null) {
        setTimeout(() => {
          openPrintWindow(false);
          onClose?.();
        }, 400);
      }
    }, [branding]);
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
        <div className="bg-card rounded-xl p-6 flex flex-col items-center gap-3 shadow-xl">
          <Icon name="FileDown" size={32} className="text-primary animate-pulse" />
          <p className="text-foreground font-semibold">Generando PDF...</p>
          <p className="text-muted-foreground text-sm">El archivo se descargará automáticamente.</p>
        </div>
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <QuotationDocument data={documentData} branding={branding || {}} />
        </div>
      </div>
    );
  }

  if (mode === 'print') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (branding !== null) {
        const timer = setTimeout(() => {
          openPrintWindow(false);
          onClose?.();
        }, 300);
        return () => clearTimeout(timer);
      }
    }, [branding]);
    return (
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        <QuotationDocument data={documentData} branding={branding || {}} />
      </div>
    );
  }

  return null;
};

export default QuotationPdfTemplate;
